<template>
  <article>
    <activity-stage
      :customRootContainerClass="'full-height'"
      :customContentContainerStyles="{ height: '100%' }"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <section v-if="currentRound">
          <div class="whiteboard">
            <img alt="" :src="currentRound?.doctorImage?.image?.src" />
            <svg id="whiteboardSvg" width="80%" height="90%" v-html="whiteboardSvg"></svg>
            <svg id="doctorBubbleSvg" height="100px" width="200px" v-html="doctorBubbleSvg"></svg>

            <span
              v-if="bubbleText"
              id="bubbleText"
              :class="{
                shortText: bubbleText.length < 15
              }"
              v-text="bubbleText"
            ></span>
            <div id="sentence">
              <span v-if="!correctStep" v-text="currentRound.incorrectSentence"></span>
              <blinking-cursor-text
                v-if="correctStep"
                :text="correctStep.answer"
                :text-step="0"
              ></blinking-cursor-text>
            </div>
          </div>
          <div class="choose">
            <div id="instructions">
              <audio-toggle-button></audio-toggle-button>
              <h1>Choose the correct sentence. Then type the sentence.</h1>
            </div>

            <form style="white-space: nowrap;">
              <checkbox-sentence
                v-for="(answer, index) in currentRound.selectList"
                @click.native="selectAnswer(answer)"
                :handleOnKeyboardEnter="() => selectAnswer(answer)"
                :has-error="!answer.isCorrect"
                :index="index"
                :is-checked="answer.answer === selectedAnswerText"
                :key="`step${index}`"
                :value="answer.answer"
              ></checkbox-sentence>
            </form>
          </div>
        </section>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import ActivityManager from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';

import ActivityStage from 'components/activity/ActivityStage.vue';
import AudioToggleButton from 'components/buttons/AudioToggleButton';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import CheckboxSentence from 'components/inputs/CheckboxSentence.vue';

import whiteboardSvg from 'assets/images/activities/sentence-doctor/sentence-doctor-whiteboard.svg';
import doctorBubbleSvg from 'assets/images/activities/sentence-doctor/sentence-doctor-bubble.svg';

export default {
  mixins: [ActivityManager, AssetLoader, ErrorLogger, TypingActivityHelper],

  components: {
    ActivityStage,
    AudioToggleButton,
    BlinkingCursorText,
    CheckboxSentence
  },

  data() {
    return {
      activityName: 'Sentence Doctor',
      whiteboardSvg,
      doctorBubbleSvg,
      correctStep: undefined,
      bubbleText: undefined,
      selectedAnswerText: ''
    };
  },

  watch: {
    correctStep() {
      if (this.selectedAnswerText) {
        this.enableKeyboard();
        this.showKeyboard();

        this.currentRound.selectList.forEach((answer, index) => {
          if (answer.answer !== this.selectedAnswerText)
            TweenMax.staggerTo(`.s-${index}`, 0.2, { autoAlpha: 0, display: 'none' }, 0.2);
        });

        TweenMax.fromTo(
          '#sentence',
          1,
          { autoAlpha: 0 },
          {
            css: { autoAlpha: 1 },
            delay: 0.25
          }
        );
      }
    }
  },

  methods: {
    async loadActivity() {
      this.createRounds({ instructionsClass: 'sentence-doctor-instructions' });
      await this.loadImages();
    },

    async loadImages() {
      this.activityData.rounds.forEach(round => {
        this.queueImage(round.doctorImage.image.src);
      });
      return this.downloadImages();
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setRound();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);
    },

    setRound() {
      this.disableKeyboard();
      this.hideKeyboard();
      this.setupNewRound();
      this.$nextTick(() => {
        this.bubbleText = this.currentRound.textBubble.startText;
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
        TweenMax.staggerFromTo('.select', 1.5, { autoAlpha: 0 }, { autoAlpha: 1 }, 0.2);
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.bubbleText = this.currentRound.textBubble.endText;
        this.setCurrentStep();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const t = new TimelineLite();

      t.delay(0.8).call(() => {
        if (this.isLastRound) {
          TweenMax.delayedCall(0.8, this.completeActivity);
        } else {
          t.delay(1)
            .to('.activity-stage', 0.8, { opacity: 0 })
            .call(this.resetSteps)
            .call(this.setRound);
        }
      });
    },

    selectAnswer(answer) {
      this.selectedAnswerText = answer.answer;

      if (answer.isCorrect) {
        this.correctStep = answer;
        this.overwriteProgressText({
          currentText: answer.answer
        });

        this.setScreenReaderAlert(`Correct. Now type the sentence - ${answer.answer}`);
      } else {
        this.rerenderScreenReaderAlert('Incorrect, Try Again');
      }

      return this.sndType();
    },

    resetSteps() {
      this.selectedAnswerText = '';
      this.correctStep = undefined;
    }
  }
};
</script>

<style lang="scss" scoped>
section {
  font-family: hwtfutura;

  .whiteboard {
    img {
      height: 400px;
      left: 1.5%;
      position: absolute;
      top: 100px;
      z-index: 1;
    }

    #doctorBubbleSvg {
      margin-left: 3.5%;
      position: relative;
      z-index: 0;
    }

    #whiteboardSvg {
      left: calc(3.5% + 50px);
      position: absolute;
      top: -33%;
      width: 800px;
      z-index: 0;

      @media (max-width: 690px) {
        top: -20%;
      }
      @media (max-width: 432px) {
        left: calc(3.5%);
      }
    }

    #bubbleText {
      display: block;
      font-size: 0.85em;
      left: calc(100px + 3.5%);
      position: absolute;
      text-align: left;
      top: 90px;
      transform: translate(-50%, -50%);
      width: 122px;
    }

    .shortText {
      padding: 1% 2.5%;
    }

    #sentence {
      position: absolute;
      font-size: 2em;
      top: 45px;
      left: calc(3.5% + 200px);
      width: 550px;

      p,
      span {
        display: block;

        padding: 0 0.5%;
        width: 100%;
      }
    }
  }

  .choose {
    display: block;
    margin: 125px 0px 0px 250px;
    width: 70%;

    #instructions {
      position: relative;
      white-space: nowrap;

      button {
        left: -7px;
        position: absolute;
        top: -10px;
      }

      h1 {
        font-family: hwtfutura-semibold;
        font-size: 16px;
        left: 35px;
        margin: 0px;
        position: relative;
      }
    }
  }
}
</style>
